import React from 'react';
import { Alert, Box, Button, Dialog, DialogContent, Stack, Typography } from '@mui/material';
import MarkEmailUnreadOutlinedIcon from '@mui/icons-material/MarkEmailUnreadOutlined';
import { useNavigate } from 'react-router-dom';
import { AppDialogTitle } from '../AppDialogTitle';
import { api } from '../../lib/api';
import { verificationFeatureLabel } from '../../lib/planGates';
import { rememberVerificationReturn } from '../../lib/verificationReturn';
import { useAuthStore } from '../../store/auth';

interface VerifyEmailGateProps {
  /** The API's `feature` key (or a readable label) for "Confirm your email to unlock …". */
  feature?: string | null;
  /** Hide "Start from a template instead" where a template is no alternative. */
  showTemplateAlternative?: boolean;
  /** Called after navigating away (e.g. to close a dialog). */
  onNavigate?: () => void;
  variant?: 'inline' | 'plain';
}

/**
 * Shared answer to EMAIL_VERIFICATION_REQUIRED: says what unlocks, where the link went, and offers
 * a resend plus a way to keep going without waiting for the email.
 */
export function VerifyEmailGate({ feature, showTemplateAlternative = true, onNavigate, variant = 'inline' }: VerifyEmailGateProps) {
  const navigate = useNavigate();
  const email = useAuthStore((s) => s.user?.email ?? null);
  const [state, setState] = React.useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const label = verificationFeatureLabel(feature);

  React.useEffect(() => {
    rememberVerificationReturn(window.location.pathname + window.location.search, label);
  }, [label]);

  async function resend() {
    setState('sending');
    try {
      await api.post('/auth/verify-email/request');
      setState('sent');
    } catch {
      setState('error');
    }
  }

  const content = (
    <Stack direction="row" spacing={1.5} sx={{ alignItems: 'flex-start' }}>
      <MarkEmailUnreadOutlinedIcon color="primary" sx={{ mt: 0.25 }} aria-hidden />
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontWeight: 800 }}>Confirm your email to unlock {label}.</Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
          {email ? <>We sent a link to <strong>{email}</strong>.</> : 'We sent you a confirmation link.'}
          {' '}Open it and you&apos;re straight back here.
        </Typography>
        {state === 'sent' ? (
          <Typography variant="body2" sx={{ color: 'success.main', mt: 1 }} role="status">New link sent{email ? ` to ${email}` : ''}. Check your inbox and spam folder.</Typography>
        ) : null}
        {state === 'error' ? (
          <Typography variant="body2" color="error" sx={{ mt: 1 }} role="alert">We couldn&apos;t send the email. Wait a minute and try again.</Typography>
        ) : null}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 1.5 }}>
          <Button variant="contained" size="small" onClick={() => void resend()} disabled={state === 'sending' || state === 'sent'}>
            {state === 'sending' ? 'Sending…' : state === 'sent' ? 'Email sent' : 'Resend email'}
          </Button>
          {showTemplateAlternative ? (
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                onNavigate?.();
                navigate('/templates');
              }}
            >
              Start from a template instead
            </Button>
          ) : null}
        </Stack>
      </Box>
    </Stack>
  );

  if (variant === 'plain') return content;
  return (
    <Alert severity="info" icon={false} sx={{ '& .MuiAlert-message': { width: '100%' } }} data-testid="verify-email-gate">
      {content}
    </Alert>
  );
}

interface VerifyEmailDialogProps {
  feature: string | null | undefined;
  open: boolean;
  onClose: () => void;
}

/** The global form of the gate, opened by the API client for any EMAIL_VERIFICATION_REQUIRED. */
export function VerifyEmailDialog({ feature, open, onClose }: VerifyEmailDialogProps) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth aria-labelledby="verify-email-title">
      <AppDialogTitle id="verify-email-title" onClose={onClose}>Confirm your email</AppDialogTitle>
      <DialogContent sx={{ pt: '20px !important' }}>
        <VerifyEmailGate feature={feature} onNavigate={onClose} variant="plain" />
      </DialogContent>
    </Dialog>
  );
}
